/**
 * AppShell — the authenticated console chrome. A left sidebar (brand, nav, a
 * collapse toggle, and a bottom user menu) sits beside a scrollable content area
 * where the routed screen renders. The user menu hosts inline language + theme
 * switchers and sign out. Token-driven (Light + Dark via @obrava/ui semantic
 * tokens, no hardcoded colors); all copy via i18n.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useTheme } from '@obrava/ui';
import { useAuth } from '../auth/AuthProvider.js';
import { Icon, type IconName } from './icons.js';
import { UserMenu } from './UserMenu.js';

const COLLAPSE_KEY = 'obrava.platform-console.sidebar-collapsed';

function NavItem(props: {
  readonly to: string;
  readonly icon: IconName;
  readonly label: string;
  readonly active: boolean;
  readonly collapsed: boolean;
}): React.ReactElement {
  const theme = useTheme();
  // When collapsed, only the icon shows — reveal the option name in a styled
  // tooltip on hover (the native `title` is slow and OS-styled, so we draw our own).
  const [hovered, setHovered] = React.useState(false);
  const showTooltip = props.collapsed && hovered;
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        to={props.to}
        aria-label={props.collapsed ? props.label : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: props.collapsed ? 'center' : 'flex-start',
          gap: theme.spacing.space[3],
          padding: props.collapsed
            ? `${theme.spacing.space[3]}px 0`
            : `${theme.spacing.space[3]}px ${theme.spacing.space[4]}px`,
          borderRadius: theme.radii.radius.md,
          backgroundColor: props.active ? theme.color.selected : 'transparent',
          color: props.active ? theme.color.accent : theme.color.textSecondary,
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.secondary,
          fontWeight: theme.typography.weight.medium,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Icon name={props.icon} size={18} />
        {!props.collapsed ? <span>{props.label}</span> : null}
      </Link>
      {showTooltip ? (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            left: `calc(100% + ${theme.spacing.space[2]}px)`,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: `${theme.spacing.space[1]}px ${theme.spacing.space[3]}px`,
            backgroundColor: theme.color.surfaceOverlay,
            color: theme.color.textPrimary,
            border: `1px solid ${theme.color.border}`,
            borderRadius: theme.radii.radius.sm,
            boxShadow: theme.radii.shadow.overlay,
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.footnote,
            fontWeight: theme.typography.weight.medium,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 60,
          }}
        >
          {props.label}
        </span>
      ) : null}
    </div>
  );
}

export function AppShell(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { platformUser } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = React.useState<boolean>(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const tenantsActive = pathname === '/' || pathname.startsWith('/tenants');

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.color.canvas }}>
      <aside
        style={{
          width: collapsed ? 76 : 256,
          flex: `0 0 ${collapsed ? 76 : 256}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.space[1],
          padding: `${theme.spacing.space[5]}px ${theme.spacing.space[4]}px`,
          backgroundColor: theme.color.canvas,
          transition: 'width 0.2s ease, flex-basis 0.2s ease',
        }}
      >
        {/* Brand */}
        <Link
          to="/tenants"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: theme.spacing.space[4],
            padding: `${theme.spacing.space[2]}px ${theme.spacing.space[3]}px ${theme.spacing.space[6]}px`,
            textDecoration: 'none',
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              flex: '0 0 36px',
              borderRadius: theme.radii.radius.md,
              backgroundColor: theme.color.primary,
              color: theme.color.onPrimary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="logo" size={18} strokeWidth={2.4} />
          </span>
          {!collapsed ? (
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <span
                style={{
                  fontFamily: theme.typography.family.display,
                  fontSize: theme.typography.size.bodyLg,
                  fontWeight: theme.typography.weight.medium,
                  color: theme.color.textPrimary,
                }}
              >
                Obrava
              </span>
              <span
                style={{
                  fontSize: theme.typography.size.caption,
                  color: theme.color.textTertiary,
                  fontWeight: theme.typography.weight.medium,
                }}
              >
                {t('platformConsole.common:consoleName')}
              </span>
            </span>
          ) : null}
        </Link>

        {/* Nav */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <NavItem
            to="/tenants"
            icon="tenants"
            label={t('platformConsole.obradors:title')}
            active={tenantsActive}
            collapsed={collapsed}
          />

          <div style={{ height: 1, backgroundColor: theme.color.divider, margin: `${theme.spacing.space[2]}px ${theme.spacing.space[3]}px` }} />

          <button
            type="button"
            onClick={toggleCollapsed}
            title={t('platformConsole.common:collapse')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: theme.spacing.space[3],
              padding: collapsed ? `${theme.spacing.space[3]}px 0` : `${theme.spacing.space[3]}px ${theme.spacing.space[4]}px`,
              border: 'none',
              background: 'transparent',
              borderRadius: theme.radii.radius.md,
              color: theme.color.textSecondary,
              fontFamily: theme.typography.family.sans,
              fontSize: theme.typography.size.secondary,
              fontWeight: theme.typography.weight.medium,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', display: 'inline-flex' }}>
              <Icon name="collapse" size={18} />
            </span>
            {!collapsed ? <span>{t('platformConsole.common:collapse')}</span> : null}
          </button>
        </nav>

        <div style={{ flex: 1 }} />

        {platformUser ? <UserMenu email={platformUser.email} collapsed={collapsed} /> : null}
      </aside>

      {/* The content sits on an inset, rounded `surfaceSunken` panel over the canvas; the cards
          inside it are white (same chrome as the backoffice). */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          maxHeight: `calc(100vh - ${theme.spacing.space[4] * 2}px)`,
          overflow: 'auto',
          backgroundColor: theme.color.surfaceSunken,
          margin: `${theme.spacing.space[4]}px ${theme.spacing.space[4]}px ${theme.spacing.space[4]}px 0`,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radii.radius.xl,
        }}
      >
        <div
          style={{
            maxWidth: theme.spacing.size.shellMaxWidth,
            margin: '0 auto',
            padding: theme.spacing.size.screenPaddingBackoffice,
            boxSizing: 'border-box',
          }}
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
